
import { Users, Brain, BarChart3, Shield, Star, DollarSign } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";

interface StrategyCardProps {
  type: 'free' | 'premium';
}

const StrategyCard = ({ type }: StrategyCardProps) => {
  const isFree = type === 'free';
  
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <Card className={isFree 
        ? 'border border-corporate-primary-25 shadow-md hover:shadow-lg transition-shadow duration-300 h-full' 
        : 'bg-gradient-to-br from-corporate-primary-100 to-corporate-primary-75 text-white border-corporate-primary-25 shadow-lg hover:shadow-xl transition-shadow duration-300 h-full'
      }>
        <CardContent className="p-8">
          <div className="mb-6 h-full">
            {isFree ? (
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-corporate-primary-25 px-4 py-1 rounded-full">
                  <span className="font-bold text-corporate-primary-100">FREE</span>
                </div>
                <h3 className="text-xl font-semibold text-corporate-primary-100">Free Strategies</h3>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-corporate-secondary-100 px-4 py-1 rounded-md">
                  <span className="font-bold text-corporate-primary-100">PREMIUM</span>
                </div>
                <h3 className="text-xl font-semibold text-white">Premium Strategies</h3>
              </div>
            )}
            
            <div className="space-y-6">
              {isFree ? (
                <>
                  <div className="flex gap-4">
                    <Users className="w-6 h-6 text-corporate-primary-100 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-corporate-primary-100 mb-1">Open Access (Free)</h4>
                      <p className="text-corporate-neutral-75">Available to all users with no cost or subscription required.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Users className="w-6 h-6 text-corporate-primary-100 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-corporate-primary-100 mb-1">Community-Sourced</h4>
                      <p className="text-corporate-neutral-75">Strategies contributed by fellow traders in the METRADE community.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <BarChart3 className="w-6 h-6 text-corporate-primary-100 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-corporate-primary-100 mb-1">Basic Performance Data</h4>
                      <p className="text-corporate-neutral-75">Essential metrics to gauge each strategy's performance.</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-4">
                    <Shield className="w-6 h-6 text-corporate-secondary-100 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1 text-corporate-secondary-100">Expert-Vetted Strategies</h4>
                      <p className="text-white">Access proven strategies from professional traders vetted by METRADE's team.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <DollarSign className="w-6 h-6 text-corporate-secondary-100 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1 text-corporate-secondary-100">No Additional Costs</h4>
                      <p className="text-white">Your premium subscription covers full access to all strategy signals.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Star className="w-6 h-6 text-corporate-secondary-100 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1 text-corporate-secondary-100">Premium Support</h4>
                      <p className="text-white">Priority customer support whenever you need help or guidance.</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default StrategyCard;
